import { Plan, PlanStatus } from "@/types/index";
import { isUserPremium } from "@/lib/utils";
import type { UserPlan } from "@/types/planTypes";

// Use environment variable directly to avoid importing base.ts which has dynamic imports
// This prevents webpack from analyzing base.ts during bundling
const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL || "https://api.techxeed.com";

type GetPlansParams = {
	slug?: string | null;
	sortBy?: `${"createdAt" | "price"}:${"asc" | "desc"}`;
	isCustomPlan?: boolean;
	planType?: "subscription" | "hosting";
	status?: PlanStatus;
};

export async function getPlans({
	slug,
	sortBy,
	isCustomPlan = false,
	planType = "subscription",
	status = PlanStatus.PUBLISHED,
}: GetPlansParams): Promise<Plan[]> {
	const params = new URLSearchParams({
		limit: "100",
		sortBy: sortBy ?? "price:asc",
		isCustomPlan: isCustomPlan ? "true" : "false",
		planType: planType,
		status: status,
	});

	if (slug !== null && slug !== undefined) params.set("slug", slug);

	const isServer = typeof window === "undefined";
	const fetchSignal =
		typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function"
			? AbortSignal.timeout(isServer ? 60_000 : 45_000)
			: undefined;

	try {
		const response = await fetch(`${BACKEND_URL}/plans?${params}`, {
			// No Content-Type header for GET requests - it triggers CORS preflight
			cache: "no-store",
			signal: fetchSignal,
		});

		if (!response.ok) {
			console.error(`Failed to fetch plans: ${response.status} ${response.statusText}`);
			return []; // Return empty array on error to prevent build failure
		}

		const contentType = response.headers.get("content-type");
		if (!contentType || !contentType.includes("application/json")) {
			console.error("API returned non-JSON response, returning empty array");
			return []; // Return empty array if response is not JSON
		}

		try {
			return await response.json();
		} catch (error) {
			console.error("Error parsing plans response:", error);
			return []; // Return empty array on parse error
		}
	} catch (error) {
		// ECONNREFUSED, TLS/DNS failures, timeouts — fetch throws before response
		console.error(
			`Plans fetch failed (${BACKEND_URL}):`,
			error instanceof Error ? error.message : error,
		);
		return [];
	}
}

export const getPlan = async (id: string): Promise<Plan> => {
	const response = await fetch(`${BACKEND_URL}/plans/${id}`, {
		method: "GET",
		cache: "no-store", // Prevent Next.js from caching plan data
	});

	const result = await response.json();
	return result;
};

/**
 * True when the user is on monthly/yearly billing **and** their current plan is a premium
 * **subscription** (not a hosting plan).
 */
export async function isPremiumSubscriptionPlanMember(
	planId: string | null | undefined,
	duration: UserPlan | undefined,
): Promise<boolean> {
	if (!planId || typeof planId !== "string" || planId.trim().length === 0) return false;
	if (!isUserPremium(duration)) return false;
	try {
		const plan = await getPlan(planId);
		return plan.planType === "subscription";
	} catch {
		return false;
	}
}
